import { useMemo } from 'react';

import {
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerLoading,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { ActivityBuilderDrawerContent } from 'approot/activity-builder/activity-builder-drawer.style';
import { Heading20 } from 'approot/shared/styles/typography.style';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import { BoxPanel } from 'approot/shared/styles/box-panel.style';
import { useLiveClassroomStudents } from 'approot/activity-builder/classroom/subscription/live-classroom-students.apollo';
import { useGetClassStudents } from 'approot/activity-builder/classroom/classroom-student.hooks';
import { useWsClientStatus } from 'lib/ws/ws-apollo';
import Emoji from 'approot/shared/emoji/emoji';
import Text from 'approot/shared/styles/typography/text';
import * as Styled from './present-menu-students.style';
import { SecondaryButtonAndIconLink } from 'approot/shared/styles/buttons/secondary-button.style';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import { getUserClassPath } from 'approot/classes/classes.apollo';
import { PRIVATE_MY_STUDENTS } from 'global/private-routes.constants';
import { openNewTab } from 'approot/shared/logic/open-new-tab';
import IconExpand from 'approot/shared/icons/icon-expand';
import Accordion from 'approot/shared/accordion/accordion';
import AccordionToggleNew from 'approot/shared/accordion/accordion-toggle-new';

import studentResponsePreview from './student-response-preview.png';
import {
  NotificationContent,
  NotificationTip,
  NotificationTipText,
  NotificationTipTitle,
} from 'approot/shared/notifications/notification-tip.style';
import { getStudentDisplayName } from 'approot/students/logic/get-student-display-name';
import { addStudentsPushDataTrackingEvent } from 'approot/teacher-student/logic/add-students-push-data-tracking-event';
import { EVENT_TARGET_TYPE_CTA } from 'approot/shared/data-tracker/data-events.constants';

type Props = {
  classId: number | null;
  onClose: () => void;
};

const PresentMenuStudents = ({ classId, onClose }: Props) => {
  const { data: classStudentsData, loading } = useGetClassStudents({
    classId,
  });

  const { onlineStudents, offlineStudents } = useLiveClassroomStudents();
  const { isConnectionActive } = useWsClientStatus();

  const classStudents = useMemo(
    () => classStudentsData?.getClassStudents || [],
    [classStudentsData]
  );

  const hasStudents = classStudents.length > 0;

  if (loading) {
    return (
      <ActivityBuilderDrawerWrapper>
        <ActivityBuilderDrawerLoading title="Students" onClose={onClose} />
      </ActivityBuilderDrawerWrapper>
    );
  }

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader title="Students" onClose={onClose} />
      <ActivityBuilderDrawerContent>
        {!hasStudents ? (
          <Box $alignItems="center" $gap={4}>
            <BoxPanel $borderRadius={8} $padding={8} $marginBottom={24}>
              <Styled.EmptyStateImg
                src={studentResponsePreview}
                alt="Student response preview"
              />
            </BoxPanel>
            <Heading20 textAlignCenter>
              Add your students to unlock their responses
            </Heading20>
            <Text $align="center">
              Check for student understanding in real-time, save and download
              work samples and view your students’ portfolio.
            </Text>
          </Box>
        ) : (
          <>
            {!isConnectionActive ? (
              <>
                <NotificationTip>
                  <NotificationContent>
                    <NotificationTipTitle>
                      Student's Online Status Unavailable
                    </NotificationTipTitle>
                    <NotificationTipText marginBottom={0}>
                      Browser or network settings may be blocking this feature.
                    </NotificationTipText>
                  </NotificationContent>
                </NotificationTip>
                <Accordion id="student-list-accordion">
                  <AccordionToggleNew
                    withDividers={false}
                    displayExpanded={true}
                    labelComponent={
                      <Styled.PresentMenuStudentsContainer>
                        <Text $fontSize={14} $colour="black">
                          <strong>All Students</strong>
                        </Text>
                        <Text $fontSize={14} $colour="grey">
                          {`${classStudents.length} / ${classStudents.length}`}
                        </Text>
                      </Styled.PresentMenuStudentsContainer>
                    }
                  >
                    <BoxList>
                      {classStudents.map(student => (
                        <Box
                          as="li"
                          key={student.id}
                          $gap={12}
                          $alignItems="center"
                          $padding={16}
                        >
                          <Styled.StudentAvatar>
                            {student.emeji && (
                              <Emoji
                                id={student.emeji}
                                size={40}
                                aria-hidden="true"
                              />
                            )}
                            <Styled.StatusIndicator $isNeutral />
                          </Styled.StudentAvatar>
                          <Text>{getStudentDisplayName(student)}</Text>
                        </Box>
                      ))}
                    </BoxList>
                  </AccordionToggleNew>
                </Accordion>
              </>
            ) : (
              <Accordion id="student-list-accordion">
                {/* Offline Students */}
                {offlineStudents.length > 0 && (
                  <AccordionToggleNew
                    withDividers={false}
                    displayExpanded={false}
                    labelComponent={
                      <Styled.PresentMenuStudentsContainer>
                        <Text $fontSize={14} $colour="black">
                          <strong>Offline</strong>
                        </Text>
                        <Text $fontSize={14} $colour="grey">
                          {`${offlineStudents.length} / ${classStudents.length}`}
                        </Text>
                      </Styled.PresentMenuStudentsContainer>
                    }
                  >
                    {offlineStudents.length > 0 && (
                      <BoxList>
                        {offlineStudents.map(student => (
                          <Box
                            as="li"
                            key={student.studentId}
                            $gap={12}
                            $alignItems="center"
                            $padding={16}
                          >
                            <Styled.StudentAvatar>
                              {student.emoji && (
                                <Emoji
                                  id={student.emoji}
                                  size={40}
                                  aria-hidden="true"
                                />
                              )}
                              <Styled.StatusIndicator $isOnline={false} />
                            </Styled.StudentAvatar>
                            <Text>{student.displayName}</Text>
                          </Box>
                        ))}
                      </BoxList>
                    )}
                  </AccordionToggleNew>
                )}

                {/* Online Students */}
                <AccordionToggleNew
                  withDividers={false}
                  displayExpanded={true}
                  labelComponent={
                    <Styled.PresentMenuStudentsContainer>
                      <Text $fontSize={14} $colour="black">
                        <strong>Online</strong>
                      </Text>
                      <Text $fontSize={14} $colour="grey">
                        {`${onlineStudents.length} / ${classStudents.length}`}
                      </Text>
                    </Styled.PresentMenuStudentsContainer>
                  }
                >
                  {onlineStudents.length > 0 && (
                    <BoxList $marginBottom={24}>
                      {onlineStudents.map(student => (
                        <Box
                          as="li"
                          key={student.studentId}
                          $gap={12}
                          $alignItems="center"
                          $padding={16}
                        >
                          <Styled.StudentAvatar>
                            {student.emoji && (
                              <Emoji
                                id={student.emoji}
                                size={40}
                                aria-hidden="true"
                              />
                            )}
                            <Styled.StatusIndicator $isOnline={true} />
                          </Styled.StudentAvatar>
                          <Text>{student.displayName}</Text>
                        </Box>
                      ))}
                    </BoxList>
                  )}
                </AccordionToggleNew>
              </Accordion>
            )}
          </>
        )}

        <Styled.PresentMenuAddStudents $alignItems="center" $marginTop={20}>
          <SecondaryButtonAndIconLink
            to={getRegionalisedLink(getUserClassPath(PRIVATE_MY_STUDENTS))}
            aria-label="Add Students - opens in a new tab"
            data-testid="btn-add-students"
            onClick={e => {
              e.preventDefault();
              addStudentsPushDataTrackingEvent({
                targetType: EVENT_TARGET_TYPE_CTA,
                targetIdentifier: 'students-online-drawer',
              });
              openNewTab(
                getRegionalisedLink(getUserClassPath(PRIVATE_MY_STUDENTS))
              );
            }}
          >
            Add Students
            <IconExpand />
          </SecondaryButtonAndIconLink>
        </Styled.PresentMenuAddStudents>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default PresentMenuStudents;
